import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { ConsignmentLinesTable } from '@/features/consignment-lines-table';
import type { ConsignmentLineItem, ConsignmentLinesResponse, ConsignmentStatusCode } from '@/entities/consignments';

import type { LocalPallet } from '../model/types';
import { usePalletView } from '../model/use-pallet-view';
import { usePalletGrouping } from '../model/use-pallet-grouping';
import { PalletHeader } from './pallet-header';
import { PalletActionBar } from './pallet-action-bar';

interface PalletViewProps {
  consignmentId: string;
  consignmentStatus?: ConsignmentStatusCode;
  isReadOnly?: boolean;
  /** Callback to pass the handlePalletCreation function to parent */
  onMount?: (handler: (pallets: LocalPallet[]) => void) => void;
  /** Callback when selection changes */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Optional callback for viewing line details */
  onViewLine?: (lineId: string) => void;
}

export function PalletView({
  consignmentId,
  consignmentStatus,
  isReadOnly,
  onMount,
  onSelectionChange
}: PalletViewProps) {
  const { t } = useTranslation();

  const {
    tableData,
    pallets,
    isLoading,
    error,
    canEdit,
    selectedLineIds,
    setSelectedLineIds,
    expandedRows,
    toggleExpanded,
    moveSelectedLinesToPallet,
    unassignSelectedLines,
    deletePallet,
    handlePalletCreation
  } = usePalletView({ consignmentId, consignmentStatus, isReadOnly });

  // Pass handlePalletCreation to parent component on mount
  useEffect(() => {
    if (onMount) {
      onMount(handlePalletCreation);
    }
  }, [onMount, handlePalletCreation]);

  // Notify parent when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedLineIds);
    }
  }, [selectedLineIds, onSelectionChange]);

  // Use hook for grouping logic
  const { unassignedLines, palletGroups, hasSelectedInPallets } = usePalletGrouping(
    tableData,
    pallets,
    selectedLineIds
  );

  // Handle selection change for a specific table
  const handleSelectionChange = useCallback(
    (tableLines: ConsignmentLineItem[], newIds: string[]) => {
      // Keep selections from other tables, replace this table's selections
      const otherSelections = selectedLineIds.filter((id) => !tableLines.some((l) => l.id === id));
      setSelectedLineIds([...otherSelections, ...newIds]);
    },
    [selectedLineIds, setSelectedLineIds]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t('pallet.error.loadFailed', 'Failed to load pallet data')}</AlertDescription>
      </Alert>
    );
  }

  // Empty state
  if (unassignedLines.length === 0 && palletGroups.length === 0) {
    return (
      <Alert>
        <AlertDescription>{t('pallet.noData', 'No consignment lines available')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Action bar for selected lines */}
        {canEdit && (
          <PalletActionBar
            selectedCount={selectedLineIds.length}
            hasSelectedInPallets={hasSelectedInPallets}
            pallets={pallets}
            onMoveToPallet={moveSelectedLinesToPallet}
            onUnassign={unassignSelectedLines}
          />
        )}

        {/* Unassigned lines */}
        {unassignedLines.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">{t('pallet.unassigned', 'Unassigned Lines')}</h3>
            <ConsignmentLinesTable
              consignmentId=""
              data={{ items: unassignedLines, totals: null } as unknown as ConsignmentLinesResponse}
              isLoading={false}
              tableId="pallet-unassigned"
              selectable={canEdit}
              selectedIds={selectedLineIds.filter((id) => unassignedLines.some((l) => l.id === id))}
              onSelectionChange={(ids) => handleSelectionChange(unassignedLines, ids)}
            />
          </div>
        )}

        {/* Pallet groups */}
        {palletGroups.map(({ pallet, lines }) => {
          const isExpanded = expandedRows.has(pallet.id);

          return (
            <div key={pallet.id}>
              <PalletHeader
                pallet={pallet}
                lines={lines}
                isExpanded={isExpanded}
                onToggle={() => toggleExpanded(pallet.id)}
                onDelete={() => deletePallet(pallet.id)}
              />

              {isExpanded && lines.length > 0 && (
                <ConsignmentLinesTable
                  consignmentId=""
                  data={{ items: lines, totals: null } as unknown as ConsignmentLinesResponse}
                  isLoading={false}
                  tableId={`pallet-${pallet.id}`}
                  selectable={canEdit}
                  selectedIds={selectedLineIds.filter((id) => lines.some((l) => l.id === id))}
                  onSelectionChange={(ids) => handleSelectionChange(lines, ids)}
                />
              )}

              {isExpanded && lines.length === 0 && (
                <div className="px-4 py-3 text-sm text-muted-foreground italic border-b">
                  {t('pallet.empty', 'No lines assigned to this pallet')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
